/**
 * Divisa mostrada en el proceso QR.
 *
 * Es SIEMPRE la divisa con la que la pasarela de pago va a cargar la tarjeta
 * (Customer.AddonPayments.CURRENCY o Customer.Redsys.CURRENCY según el
 * QR.PaymentProvider), nunca la del contrato; el API la resuelve y la devuelve
 * como `PaymentCurrency` en GET /qrs/token/:token. Sin dato -> EUR.
 *
 * El usuario final no la elige: store/index.js la fija (setActiveCurrency) cada
 * vez que cambia el QR cargado, y App.js remonta las vistas al cambiar. Los
 * importes NO se convierten: solo cambia el símbolo/código con que se pintan.
 *
 * Tabla SYMBOLS idéntica a la del kiosco (lockers-app qml/currencies.js) y a la
 * de los paneles (lockers-client utils/currency.js).
 */

export const DEFAULT_CURRENCY = 'EUR'

export const SYMBOLS = {
  // Europa
  EUR: '€', GBP: '£', CHF: 'fr', CZK: 'Kč', NOK: 'kr', SEK: 'kr', DKK: 'kr', ISK: 'kr', PLN: 'zł',
  HUF: 'Ft', RON: 'lei', BGN: 'лв', RSD: 'дин', MKD: 'ден', BAM: 'KM', ALL: 'lek', MDL: 'lei',
  UAH: '₴', BYN: 'Br', RUB: '₽', TRY: '₺', GEL: '₾', AMD: 'AMD', AZN: '₼', KZT: '₸', UZS: 'UZS',
  GIP: '£', HRK: 'kn',
  // América
  USD: '$', CAD: 'C$', MXN: '$', BRL: 'R$', ARS: '$', CLP: '$', COP: '$', PEN: 'S/', UYU: '$U',
  DOP: 'RD$', CRC: '₡', GTQ: 'Q', PAB: 'B/.', BOB: 'Bs', PYG: '₲', VES: 'Bs',
  // Asia - Pacífico
  JPY: '¥', CNY: '¥', KRW: '₩', TWD: 'NT$', HKD: 'HK$', SGD: 'S$', MYR: 'RM', THB: '฿', VND: '₫',
  PHP: '₱', IDR: 'Rp', INR: '₹', PKR: 'Rs', BDT: '৳', LKR: 'Rs', NPR: 'Rs', MVR: 'Rf', MMK: 'K',
  AUD: 'A$', NZD: 'NZ$', FJD: 'FJ$', XPF: 'F',
  // Oriente Medio
  AED: 'AED', SAR: 'SAR', QAR: 'QAR', KWD: 'KWD', BHD: 'BHD', OMR: 'OMR', JOD: 'JOD', ILS: '₪',
  // África
  EGP: 'E£', MAD: 'MAD', TND: 'TND', DZD: 'DZD', LYD: 'LYD', ZAR: 'R', NGN: '₦', KES: 'KSh',
  GHS: '₵', TZS: 'TSh', UGX: 'USh', XOF: 'CFA', XAF: 'CFA', MUR: 'Rs', ETB: 'Br',
}

/** Código válido (en tabla) en mayúsculas, o '' si no lo es. */
export function normalizeCurrency(code) {
  const c = code === undefined || code === null ? '' : String(code).trim().toUpperCase()
  return SYMBOLS[c] !== undefined ? c : ''
}

/** Símbolo de una divisa; si no está en la tabla devuelve el propio código, nunca vacío. */
export function symbolFor(code) {
  const c = code === undefined || code === null ? '' : String(code).trim().toUpperCase()
  if (c === '') return SYMBOLS[DEFAULT_CURRENCY]
  return SYMBOLS[c] !== undefined ? SYMBOLS[c] : c
}

let activeCode = DEFAULT_CURRENCY

export function setActiveCurrency(code) {
  activeCode = normalizeCurrency(code) || DEFAULT_CURRENCY
}

export function currencyCode() {
  return activeCode
}

export function currencySymbol() {
  return symbolFor(activeCode)
}

/** "1.234,56 €": número en es-ES + símbolo como sufijo (igual que el kiosco). */
export function formatMoney(value, opts = {}) {
  const n = Number(value)
  if (!Number.isFinite(n)) return typeof value === 'string' && value.trim() !== '' ? value : '—'
  const { minimumFractionDigits = 2, maximumFractionDigits = 2, locale = 'es-ES' } = opts
  return `${n.toLocaleString(locale, { minimumFractionDigits, maximumFractionDigits })} ${currencySymbol()}`
}
